import { ArrowRight } from 'lucide-react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import type { FunnelCarrier } from '@/models/funnelSession'
import { ClaimedDotSignIn } from './ClaimedDotSignIn'

/**
 * Confirm-your-business step. On desktop it renders inline in the left column;
 * on mobile the parent drops the same panel into a modal. Presentation only —
 * the parent owns the confirm / reject actions. Field mapping mirrors the
 * production ConfirmCarrierCard.
 *
 * Asks for nothing: the contact details used to be captured here (INS-337) and
 * now come after the estimates (INS-370), so this step is one tap.
 */
export function BusinessConfirmPanel({
  carrier,
  sessionId,
  isConfirming,
  error,
  onConfirm,
  onReject,
}: {
  carrier: FunnelCarrier | null
  sessionId?: string
  isConfirming: boolean
  /** A terminal error (`canRetry: false`) takes the confirm CTA away — pressing it again only reproduces it. */
  error?: { message: string; canRetry: boolean; claimedDot?: boolean }
  onConfirm: () => void
  onReject: () => void
}) {
  const canConfirm = error === undefined || error.canRetry
  const showSignIn = error !== undefined && error.claimedDot === true && sessionId !== undefined
  const powerUnits = carrier?.powerUnits ?? 0
  const driverCount = carrier?.drivers ?? 0
  const fleet = powerUnits > 0 ? `${powerUnits} truck${powerUnits === 1 ? '' : 's'}` : '-'
  const drivers = driverCount > 0 ? `${driverCount} driver${driverCount === 1 ? '' : 's'}` : '-'
  const operating = carrier?.state || '-'
  const cargo = carrier?.cargoCarried.join(', ') || '-'

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <span className="eyebrow self-start">We found</span>
        <h3 className="text-2xl font-bold md:text-3xl">{carrier?.name || 'Unknown carrier'}</h3>
      </div>

      <div className="bg-card overflow-hidden rounded-md border">
        <FieldRow>
          <Cell label="DOT" value={carrier?.dotNumber || '-'} />
          <Cell label="Operating" value={operating} />
        </FieldRow>
        <FieldRow withTopBorder>
          <Cell label="Fleet" value={fleet} />
          <Cell label="Drivers" value={drivers} />
        </FieldRow>
        <FieldRow withTopBorder>
          <Cell label="Cargo" value={cargo} fullWidth />
        </FieldRow>
      </div>

      <div className="flex flex-col gap-3">
        {error && (
          <Banner variant="error" small>
            {error.message}
          </Banner>
        )}
        {canConfirm && (
          <Button
            onClick={onConfirm}
            disabled={isConfirming}
            loading={isConfirming ? 'Getting estimates…' : undefined}
            className="w-full"
          >
            Yes, that&apos;s me
            {!isConfirming && <ArrowRight className="size-4" />}
          </Button>
        )}
        {/* The DOT is spoken for, so the visitor is most likely the customer — offer the way in rather than only the way out. */}
        {showSignIn && <ClaimedDotSignIn sessionId={sessionId} />}
        <Button
          variant={canConfirm || showSignIn ? 'ghost' : 'default'}
          onClick={onReject}
          disabled={isConfirming}
          className="w-full"
        >
          No, try another DOT
        </Button>
      </div>
    </div>
  )
}

function FieldRow({ children, withTopBorder }: { children: React.ReactNode; withTopBorder?: boolean }) {
  return <div className={`grid grid-cols-2 divide-x ${withTopBorder ? 'border-t' : ''}`}>{children}</div>
}

function Cell({ label, value, fullWidth }: { label: string; value: string; fullWidth?: boolean }) {
  return (
    <div className={`flex flex-col gap-1 p-3 sm:p-4 ${fullWidth ? 'col-span-2' : ''}`}>
      <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{label}</span>
      <span className="text-foreground text-sm font-semibold">{value}</span>
    </div>
  )
}
